import React, { useState } from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box'; // Added for potential outer styling if needed
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import api from '../../services/api';

const ConversationListDisplay = ({ conversations, selectedConversationId, onSelectConversation, token, onConversationsUpdate }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Handle case where conversations might be an object with a conversations property instead of an array directly
  const conversationsArray = Array.isArray(conversations) 
    ? conversations 
    : (conversations?.conversations || []);
  
  const handleDeleteClick = (e, convo) => {
    e.stopPropagation(); // Prevent selecting the conversation
    setConversationToDelete(convo);
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!conversationToDelete || !token) return;
    
    setDeleteLoading(true);
    try {
      await api.softDeleteConversation(conversationToDelete._id, deleteReason, token);
      
      // Refresh conversations list
      if (onConversationsUpdate) {
        onConversationsUpdate();
      }
      
      // Close dialog and reset state
      setDeleteDialogOpen(false);
      setConversationToDelete(null);
      setDeleteReason('');
    } catch (error) {
      console.error('Error deleting conversation:', error);
      setErrorMessage('Failed to delete conversation: ' + (error.response?.data?.msg || error.message));
    } finally {
      setDeleteLoading(false);
    }
  };
  
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setConversationToDelete(null);
    setDeleteReason('');
  };
  
  const handleRestoreClick = async (e, convo) => {
    e.stopPropagation(); // Prevent selecting the conversation
    
    if (!token) return;
    
    try {
      await api.undeleteConversation(convo._id, token);
      
      // Refresh conversations list
      if (onConversationsUpdate) {
        onConversationsUpdate();
      }
    } catch (error) {
      console.error('Error restoring conversation:', error);
      setErrorMessage('Failed to restore conversation: ' + (error.message || 'Unknown error'));
    }
  };
  
  if (!conversationsArray || conversationsArray.length === 0) {
    return <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>No conversations found for this client.</Typography>;
  }

  return (
    <>
      <Paper elevation={2} sx={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
        <Typography variant="subtitle1" sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          Conversation History
        </Typography>
        <List dense>
          {conversationsArray.map(convo => (
            <ListItemButton
              key={convo._id}
              selected={selectedConversationId === convo._id}
              onClick={() => onSelectConversation(convo)}
              sx={{ pr: 1 }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>Started: {new Date(convo.createdAt).toLocaleString()}</span>
                    {convo.isDeleted && (
                      <Chip 
                        label="Deleted" 
                        size="small" 
                        color="error" 
                        variant="outlined"
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" component="span">
                      {convo.messages.length} messages
                    </Typography>
                    {convo.isDeleted && convo.deletedAt && (
                      <Typography variant="caption" component="div" color="error">
                        Deleted on {new Date(convo.deletedAt).toLocaleString()}
                        {convo.deletionReason && ` - ${convo.deletionReason}`}
                      </Typography>
                    )}
                  </Box>
                }
              />
              {convo.isDeleted ? (
                <IconButton
                  edge="end"
                  aria-label="restore"
                  onClick={(e) => handleRestoreClick(e, convo)}
                  size="small"
                  sx={{ ml: 1 }}
                  color="primary"
                >
                  <RestoreIcon fontSize="small" />
                </IconButton>
              ) : (
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={(e) => handleDeleteClick(e, convo)}
                  size="small"
                  sx={{ ml: 1 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </ListItemButton>
          ))}
        </List>
      </Paper>
      
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMessage('')} variant="filled">
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Hide Conversation from Client</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will hide the conversation from the client's view but preserve it for audit purposes.
            The client will see their next non-deleted conversation instead.
          </Typography>
          {conversationToDelete && (
            <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
              Conversation started: {new Date(conversationToDelete.createdAt).toLocaleString()}
              <br />
              Messages: {conversationToDelete.messages.length}
            </Typography>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for hiding (optional)"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            placeholder="e.g., Spurious conversation created by bug, duplicate conversation, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Hiding...' : 'Hide Conversation'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConversationListDisplay;
